/**
 * AI access levels (#1537) and team budgets (#1226): the one place that turns
 * the server's refusal into the words a person reads. The server refuses a
 * feature with
 *
 *   403 { code: 'ai_access_level', reason: 'no_team' | 'level_too_low',
 *         level, required, feature, label, admins, team_managers, error }
 *
 * a team budget with
 *
 *   403 { code: 'ai_budget', reason: 'budget_exhausted' | 'no_budget' |
 *         'budget_ended' | 'budget_not_started',
 *         team_id, team_name, feature, label, admins, team_managers, error,
 *         ends_at?, starts_at? }
 *
 * where 'no_budget' is a team that has never been given a budget (Adam,
 * 6 Oct: with the switch on, such a team is refused rather than unlimited),
 * 'budget_ended' a team whose budget year ended with no next one begun
 * (ends_at is its exclusive end) and 'budget_not_started' a team whose year
 * starts later (starts_at). Budget years are annual budgets, migration 000145.
 *
 * and a failed lookup with 503 { code: 'ai_access_unavailable', error }.
 * `team_managers` are the managers appointed over the refused person's team
 * (empty for no_team, or when none is appointed); a sentence names them in
 * place of the Realm admins.
 * Levels and labels come from the server (GET /ai-access/:customer/me); none
 * is written down here. The budget refusal's sentence is built server-side
 * (PR2, `aiaccess.ReasonBudgetExhausted`) — this file only reads `error`,
 * never composes budget wording itself.
 */
import type { AIRealmAdmin } from '../types/gotypes'

export const AI_ACCESS_CODE = 'ai_access_level'
export const AI_BUDGET_CODE = 'ai_budget'
export const AI_ACCESS_UNAVAILABLE_CODE = 'ai_access_unavailable'

/** Is this 403 body code one of the two AI refusals (level or budget)? */
export const isAiRefusalCode = (code: unknown): boolean => code === AI_ACCESS_CODE || code === AI_BUDGET_CODE

/** Is this axios-shaped error an AI access or budget refusal (403 with one of the two codes)? */
export const isAiRefusalError = (error: any): boolean => isAiRefusalCode(error?.response?.data?.code)

/**
 * The admins a refusal names: names (email when there is none) joined with
 * " or ", or "your Realm admin" when the list is empty.
 */
export const adminNames = (admins: AIRealmAdmin[] | undefined | null): string => {
  const names = (admins ?? []).map((a) => a.name || a.email).filter(Boolean)
  return names.length ? names.join(' or ') : 'your Realm admin'
}

const named = (people: AIRealmAdmin[] | undefined | null): AIRealmAdmin[] => (people ?? []).filter((a) => a.name || a.email)

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** A UTC calendar date as a sentence says it: "1 October 2027". */
const utcDate = (ms: number): string => {
  const d = new Date(ms)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** The day a budget year starts on (its start, UTC). */
export const budgetYearStartDate = (startMs: number): string => utcDate(startMs)

/** The last day inside a budget year whose exclusive end is endMs (UTC): a year to 1 October 2027 00:00 ends "30 September 2027". */
export const budgetYearLastDate = (endMs: number): string => utcDate(endMs - 1)

export type NoBudgetReason = 'no_budget' | 'budget_ended' | 'budget_not_started'

/** Why a team with no budget now is refused, from its state (/me's budget.state). */
export const noBudgetReason = (state: string | undefined | null): NoBudgetReason =>
  state === 'ended' ? 'budget_ended' : state === 'scheduled' ? 'budget_not_started' : 'no_budget'

/**
 * The caller's team managers by name, with no trailing comma: "your team
 * manager, Jo Bloggs", "your team managers, Jo Bloggs or Sam Smith", or
 * "your team manager" when none is appointed.
 */
export const teamManagersPhrase = (managers: AIRealmAdmin[] | undefined | null): string => {
  const list = named(managers)
  if (!list.length) return 'your team manager'
  return `${list.length > 1 ? 'your team managers' : 'your team manager'}, ${adminNames(list)}`
}

/**
 * Who a budget refusal sends a person to (Adam, 6 Oct): the team's managers
 * by name when it has any; otherwise "your team manager", followed by the
 * Realm admins' names when there are any.
 */
export const teamManager = (admins: AIRealmAdmin[] | undefined | null, managers?: AIRealmAdmin[] | null): string => {
  if (named(managers).length) return `${teamManagersPhrase(managers)},`
  return named(admins).length ? `your team manager, ${adminNames(admins)},` : 'your team manager'
}

/**
 * Matt's refusal sentences (#1226), and the budget year ones (Adam, 6 Oct),
 * naming the team's managers when there are any. The server's `error` field
 * is the same sentence (goapi/pkg/server/ai_access_gate.go refusalSentence);
 * change both together. ends_at and starts_at are the year's dates for
 * budget_ended and budget_not_started (epoch ms).
 */
export const refusalSentence = (refusal: {
  reason?: string
  admins?: AIRealmAdmin[] | null
  team_managers?: AIRealmAdmin[] | null
  ends_at?: number | null
  starts_at?: number | null
}): string => {
  const { admins, team_managers: managers } = refusal
  switch (refusal.reason) {
    case 'budget_exhausted':
      return `You have reached your team's budget. Please contact ${teamManager(admins, managers)} to discuss a change.`
    case 'no_budget':
      return `Your team has no AI budget yet. Please contact ${teamManager(admins, managers)} to set one.`
    case 'budget_ended': {
      const when = typeof refusal.ends_at === 'number' ? `ended on ${budgetYearLastDate(refusal.ends_at)}` : 'has ended'
      return `Your team's AI budget year ${when}. Please contact ${teamManager(admins, managers)} to set the next one.`
    }
    case 'budget_not_started': {
      const when = typeof refusal.starts_at === 'number' ? `starts on ${budgetYearStartDate(refusal.starts_at)}` : 'has not started yet'
      return `Your team's AI budget year ${when}. Please contact ${teamManager(admins, managers)} if you need AI before then.`
    }
    case 'no_team':
      // No team, so no team manager: always the Realm admins.
      return `You're not in a budget team yet. Speak to ${adminNames(admins)}.`
    default:
      // A team manager sets levels in their team, so they are named here too.
      return `There is budget in your team, but you don't have access to this function. Speak to ${adminNames(named(managers).length ? managers : admins)}.`
  }
}

/**
 * The message for an AI access refusal, or null when the error is anything
 * else. Takes an axios-shaped error.
 */
export const aiAccessRefusalMessage = (error: any): string | null => {
  const data = error?.response?.data
  if (!data || typeof data !== 'object') return null
  if (isAiRefusalCode(data.code)) {
    // An ai_budget refusal with no reason reads as a used budget.
    const reason = typeof data.reason === 'string' ? data.reason : data.code === AI_BUDGET_CODE ? 'budget_exhausted' : undefined
    return refusalSentence({
      reason,
      admins: Array.isArray(data.admins) ? data.admins : [],
      team_managers: Array.isArray(data.team_managers) ? data.team_managers : [],
      ends_at: typeof data.ends_at === 'number' ? data.ends_at : null,
      starts_at: typeof data.starts_at === 'number' ? data.starts_at : null,
    })
  }
  if (data.code === AI_ACCESS_UNAVAILABLE_CODE) {
    return typeof data.error === 'string' && data.error ? data.error : 'Could not check your AI access. Try again in a moment.'
  }
  return null
}

/**
 * A refusal the agent pages draw as the notice card (#1537 PR3), from a 403
 * body: its `code` and the sentence to show. Codes this file knows get their
 * own wording; any other code shows the server's `error` as sent, so a new
 * refusal code needs no change here. Null when the response is not a 403
 * carrying a code.
 */
export const agentRefusalFrom = (status: number, data: unknown): { code: string; text: string } | null => {
  if (status !== 403 || !data || typeof data !== 'object') return null
  const { code, error } = data as { code?: unknown; error?: unknown }
  if (typeof code !== 'string' || !code) return null
  const text = aiAccessRefusalMessage({ response: { data } }) ?? (typeof error === 'string' && error ? error : null)
  return text ? { code, text } : null
}
